import React, { useMemo, useState } from "react";
import Header from "./components/Header.jsx";
import Sidebar from "./components/Sidebar.jsx";
import { useApiData } from "./hooks/useFetch.js";
import { api } from "./api/api.js";
import { ANNEES_ANCRAGE, TABS } from "./constants.js";

import PhenologieTab from "./components/tabs/PhenologieTab.jsx";
import EauTab from "./components/tabs/EauTab.jsx";
import AleasTab from "./components/tabs/AleasTab.jsx";
import PertesTab from "./components/tabs/PertesTab.jsx";
import MargeTab from "./components/tabs/MargeTab.jsx";
import SubventionTab from "./components/tabs/SubventionTab.jsx";
import TerritoireTab from "./components/tabs/TerritoireTab.jsx";
import EconomieTab from "./components/tabs/EconomieTab.jsx";

export default function DiagoEau() {
  const [onglet, setOnglet] = useState("phenologie");

  // ---- filtres globaux (équivalent des `input$...` partagés Shiny) ----
  const [cultureOption, setCultureOption] = useState(null); // { value: idProdu, label, semis }
  const [region, setRegion] = useState(null); // { value: gridno, label: libelle }
  const [modeRcp, setModeRcp] = useState("hybride");
  const [prixVente, setPrixVente] = useState(160);

  // ---- filtres spécifiques par onglet ----
  const [aleaSel, setAleaSel] = useState("EE");
  const [pertesAlea, setPertesAlea] = useState("Total");
  const [pertesRisque, setPertesRisque] = useState("Exceptionnel");

  const [typeMarge, setTypeMarge] = useState("annuelle");
  const [parHa, setParHa] = useState(false);
  const [chargesFixes, setChargesFixes] = useState(1359.93);
  const [equipement, setEquipement] = useState("enrouleur");
  const [lissage, setLissage] = useState(5);
  const [prixEau, setPrixEau] = useState(
    Object.fromEntries(ANNEES_ANCRAGE.map((a) => [a, 0.1]))
  );
  const [prixElec, setPrixElec] = useState(
    Object.fromEntries(ANNEES_ANCRAGE.map((a) => [a, 0.19]))
  );

  const [tauxActu, setTauxActu] = useState(4);
  const [polSubv, setPolSubv] = useState("stricte_3_15ans");

  const [pctIrrig, setPctIrrig] = useState(50);
  const [polTerr, setPolTerr] = useState("stricte_3_15ans");

  // ---- config initiale (cultures + libellés dispo) ----
  const { data: config, loading: configLoading, error: configError } =
    useApiData(api.getConfig, {});

  // initialise culture/région par défaut dès que la config arrive
  React.useEffect(() => {
    if (config && !cultureOption) setCultureOption(config.cultures?.[0] ?? null);
    if (config && !region) setRegion(config.libelles?.[0] ?? null);
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const idProdu = cultureOption?.idProdu ?? null;
  const semis = cultureOption?.semis ?? null;
  const cultureLabel = cultureOption?.label ?? null;
  const gridno = region?.value ?? null;
  const libelleLabel = region?.label ?? null;

  const baseParams = useMemo(
    () => ({ idProdu, semis, gridno, rcp: modeRcp }),
    [idProdu, semis, gridno, modeRcp]
  );

  const prixEauList = useMemo(
    () =>
      ANNEES_ANCRAGE.map((a) => ({ annee_ancrage: a, c_eau: prixEau[a] ?? 0.1 })),
    [prixEau]
  );

  const prixElecList = useMemo(
    () =>
      ANNEES_ANCRAGE.map((a) => ({ annee_ancrage: a, c_elec: prixElec[a] ?? 0.19 })),
    [prixElec]
  );

  const [exporting, setExporting] = useState(false);
  async function handleExportPptx() {
    setExporting(true);
    try {
      const blob = await api.exportPptx({
        ...baseParams,
        libelle: libelleLabel,
        culture: cultureLabel,
        prix_vente: prixVente,
        charges_fixes: chargesFixes,
        equipement,
        lissage,
        par_ha: parHa,
        prix_eau: prixEauList,
        prix_elec: prixElecList,
        pol_subv: polSubv,
        taux_actu: tauxActu,
        pol_terr: polTerr,
        pct_irrig: pctIrrig,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DiagoRisk_${cultureLabel}_${libelleLabel}_${new Date()
        .toISOString()
        .slice(0, 10)}.pptx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Échec de l'export PPTX : " + (e.message || e));
    } finally {
      setExporting(false);
    }
  }

  if (configLoading) return <div className="diagoeau-state-msg">Chargement de la configuration…</div>;
  if (configError)
    return <div className="diagoeau-state-msg diagoeau-error">Erreur de connexion à l'API : {configError}</div>;

  const sidebarProps = {
    config,
    cultureOption,
    setCultureOption,
    region,
    setRegion,
    modeRcp,
    setModeRcp,
    prixVente,
    setPrixVente,
    onglet,
    aleaSel,
    setAleaSel,
    pertesAlea,
    setPertesAlea,
    pertesRisque,
    setPertesRisque,
    typeMarge,
    setTypeMarge,
    parHa,
    setParHa,
    chargesFixes,
    setChargesFixes,
    equipement,
    setEquipement,
    lissage,
    setLissage,
    prixEau,
    setPrixEau,
    prixElec,
    setPrixElec,
    tauxActu,
    setTauxActu,
    polSubv,
    setPolSubv,
    pctIrrig,
    setPctIrrig,
    polTerr,
    setPolTerr,
  };

  return (
    <div className="diagoeau-page">
      <Header onExport={handleExportPptx} exporting={exporting} />
      <div className="diagoeau-layout">
        <Sidebar {...sidebarProps} />
        <main className="diagoeau-main">
          <div className="diagoeau-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={"diagoeau-tab-btn" + (onglet === t.id ? " diagoeau-active" : "")}
                onClick={() => setOnglet(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {onglet === "phenologie" && (
            <PhenologieTab baseParams={baseParams} culture={cultureLabel} libelle={libelleLabel} />
          )}
          {onglet === "eau" && (
            <EauTab baseParams={baseParams} culture={cultureLabel} libelle={libelleLabel} modeRcp={modeRcp} />
          )}
          {onglet === "aleas" && (
            <AleasTab baseParams={baseParams} aleaSel={aleaSel} culture={cultureLabel} libelle={libelleLabel} />
          )}
          {onglet === "pertes" && (
            <PertesTab
              baseParams={baseParams}
              alea={pertesAlea}
              risque={pertesRisque}
              culture={cultureLabel}
              libelle={libelleLabel}
            />
          )}
          {onglet === "marge" && (
            <MargeTab
              baseParams={baseParams}
              prixVente={prixVente}
              chargesFixes={chargesFixes}
              equipement={equipement}
              lissage={lissage}
              parHa={parHa}
              typeMarge={typeMarge}
              prixEauList={prixEauList}
              prixElecList={prixElecList}
              culture={cultureLabel}
              libelle={libelleLabel}
              modeRcp={modeRcp}
            />
          )}
          {onglet === "subvention" && (
            <SubventionTab
              baseParams={baseParams}
              prixVente={prixVente}
              chargesFixes={chargesFixes}
              tauxActu={tauxActu}
              polSubv={polSubv}
              prixEauList={prixEauList}
              culture={cultureLabel}
              libelle={libelleLabel}
            />
          )}
          {onglet === "territoire" && (
            <TerritoireTab
              baseParams={baseParams}
              pctIrrig={pctIrrig}
              polTerr={polTerr}
              culture={cultureLabel}
              libelle={libelleLabel}
            />
          )}
          {onglet === "economie" && <EconomieTab />}
        </main>
      </div>
    </div>
  );
}